import React from "react";

import { getServiceUri } from "@auto/metadata";
import { t } from "@auto/services";

import styles from "./ApiDocumentationPage.module.styl";

const shellQuote = (value) => `'${String(value).replace(/'/g, `'"'"'`)}'`;

const joinUrl = (base, path) => {
  if (!base) {
    throw new Error(t("developer.apiDocs.emptyServiceUri"));
  }
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

const readJsonObject = (text, description) => {
  const parsed = JSON.parse(text);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
    throw new Error(t("developer.apiDocs.mustBeJsonObject", { name: description }));
  }
  return parsed;
};

const requestCanBeRepresented = (endpoint) => {
  const request = endpoint.request || {};

  if (request.body && (endpoint.httpMethod === "GET" || endpoint.httpMethod === "HEAD")) {
    return {
      allowed: false,
      reason: t("developer.apiDocs.methodBodyUnsupported", { method: endpoint.httpMethod }),
    };
  }

  return { allowed: true, reason: "" };
};

const buildRequestUrl = (endpoint, pathValues, queryValues) => {
  let path = endpoint.path;

  for (const parameter of endpoint.request?.pathParameters || []) {
    const value = pathValues[parameter.name];
    if (value === undefined || value === null || String(value) === "") {
      throw new Error(t("developer.apiDocs.pathParameterRequired", { name: parameter.name }));
    }
    path = path.replace(`{${parameter.name}}`, encodeURIComponent(String(value)));
  }

  if (/\{[^}]+\}/.test(path)) {
    throw new Error(t("developer.apiDocs.unresolvedPathVariable", { path }));
  }

  const url = new URL(joinUrl(getServiceUri(), path), window.location.origin);

  for (const parameter of endpoint.request?.queryParameters || []) {
    const value = queryValues[parameter.name];
    if (value !== undefined && value !== null && String(value) !== "") {
      url.searchParams.set(parameter.name, String(value));
    }
  }

  return url;
};

const buildCurl = ({ endpoint, url, headers, bodyText, hasBody }) => {
  const parts = ["curl", "-X", endpoint.httpMethod, shellQuote(url.toString())];

  for (const [name, value] of Object.entries(headers)) {
    parts.push("-H", shellQuote(`${name}: ${value}`));
  }

  if (hasBody) {
    parts.push("--data-raw", shellQuote(bodyText));
  }

  return parts.join(" ");
};

const ResponsePanel = ({ response }) => {
  if (!response) return null;

  return (
    <div className={styles.responsePanel}>
      <div className={styles.responseSummary}>
        <span className={response.ok ? styles.responseStatusOk : styles.responseStatusError}>
          {response.status} {response.statusText}
        </span>
        <span>{response.durationMs} ms</span>
      </div>

      <div className={styles.responseBlock}>
        <div className={styles.responseLabel}>{t("developer.apiDocs.headers")}</div>
        <pre>{response.headers}</pre>
      </div>

      <div className={styles.responseBlock}>
        <div className={styles.responseLabel}>{t("developer.apiDocs.body")}</div>
        <pre>{response.body || t("developer.apiDocs.empty")}</pre>
      </div>
    </div>
  );
};

export const ApiRequestConsole = ({ endpoint, enabled, disabledReason }) => {
  const representation = requestCanBeRepresented(endpoint);
  const canExecute = enabled && representation.allowed;
  const finalDisabledReason = !enabled ? disabledReason : representation.reason;
  const hasBody = Boolean(endpoint.request?.body);

  const [pathValues, setPathValues] = React.useState({});
  const [queryValues, setQueryValues] = React.useState({});
  const [headersText, setHeadersText] = React.useState(
    hasBody
      ? '{\n  "Accept": "application/json",\n  "Content-Type": "application/json"\n}'
      : '{\n  "Accept": "application/json"\n}'
  );
  const [bodyText, setBodyText] = React.useState(hasBody ? "{}" : "");
  const [response, setResponse] = React.useState(null);
  const [error, setError] = React.useState("");
  const [running, setRunning] = React.useState(false);

  const setParameter = (setter, name, value) => {
    setter((current) => ({ ...current, [name]: value }));
  };

  const prepareRequest = () => {
    const headers = readJsonObject(headersText, t("developer.apiDocs.headers"));
    const url = buildRequestUrl(endpoint, pathValues, queryValues);

    if (hasBody) {
      JSON.parse(bodyText);
    }

    return { headers, url };
  };

  const execute = async () => {
    if (!canExecute) return;

    setRunning(true);
    setError("");
    setResponse(null);

    try {
      const { headers, url } = prepareRequest();
      const started = performance.now();

      const fetchResponse = await fetch(url.toString(), {
        method: endpoint.httpMethod,
        credentials: "include",
        headers,
        ...(hasBody ? { body: bodyText } : {}),
      });

      const body = await fetchResponse.text();
      const durationMs = Math.round(performance.now() - started);
      const responseHeaders = Array.from(fetchResponse.headers.entries())
        .map(([name, value]) => `${name}: ${value}`)
        .join("\n");

      setResponse({
        ok: fetchResponse.ok,
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        durationMs,
        headers: responseHeaders,
        body,
      });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setRunning(false);
    }
  };

  const copyCurl = async () => {
    setError("");
    try {
      const { headers, url } = prepareRequest();
      const curl = buildCurl({ endpoint, url, headers, bodyText, hasBody });
      await navigator.clipboard.writeText(curl);
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : String(copyError));
    }
  };

  return (
    <div className={styles.console}>
      {!canExecute ? (
        <div className={styles.consoleDisabled}>{finalDisabledReason}</div>
      ) : null}

      {(endpoint.request?.pathParameters || []).length ? (
        <div className={styles.consoleGroup}>
          <div className={styles.consoleGroupTitle}>{t("developer.apiDocs.pathParameters")}</div>
          <div className={styles.consoleFields}>
            {endpoint.request.pathParameters.map((parameter) => (
              <label key={parameter.name} className={styles.consoleField}>
                <span>{parameter.name}</span>
                <input
                  value={pathValues[parameter.name] || ""}
                  disabled={!canExecute}
                  onChange={(event) => setParameter(setPathValues, parameter.name, event.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {(endpoint.request?.queryParameters || []).length ? (
        <div className={styles.consoleGroup}>
          <div className={styles.consoleGroupTitle}>{t("developer.apiDocs.queryParameters")}</div>
          <div className={styles.consoleFields}>
            {endpoint.request.queryParameters.map((parameter) => (
              <label key={parameter.name} className={styles.consoleField}>
                <span>{parameter.name}</span>
                <input
                  value={queryValues[parameter.name] || ""}
                  disabled={!canExecute}
                  onChange={(event) => setParameter(setQueryValues, parameter.name, event.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles.consoleGroup}>
        <div className={styles.consoleGroupTitle}>{t("developer.apiDocs.headers")}</div>
        <textarea
          className={styles.codeEditor}
          value={headersText}
          disabled={!canExecute}
          spellCheck={false}
          onChange={(event) => setHeadersText(event.target.value)}
        />
      </div>

      {hasBody ? (
        <div className={styles.consoleGroup}>
          <div className={styles.consoleGroupTitle}>{t("developer.apiDocs.body")}</div>
          <textarea
            className={styles.codeEditorLarge}
            value={bodyText}
            disabled={!canExecute}
            spellCheck={false}
            onChange={(event) => setBodyText(event.target.value)}
          />
        </div>
      ) : null}

      <div className={styles.consoleActions}>
        <button type="button" className={styles.executeButton} disabled={!canExecute || running} onClick={execute}>
          {running ? t("developer.apiDocs.executing") : t("developer.apiDocs.execute")}
        </button>
        <button type="button" className={styles.secondaryButton} disabled={!canExecute} onClick={copyCurl}>
          {t("developer.apiDocs.copyCurl")}
        </button>
        <span className={styles.consoleNote}>{t("developer.apiDocs.sessionNote")}</span>
      </div>

      {error ? <div className={styles.consoleError}>{error}</div> : null}
      <ResponsePanel response={response} />
    </div>
  );
};

export default ApiRequestConsole;
